import React from "react";

import {
  Box,
  Grid,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  useTheme,
} from "@mui/material";

import WarningIcon from "@mui/icons-material/Warning";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";

import {
  formatDate,
  formatDecimal,
  ucFirstLetters,
} from "@aclymatepackages/formatters";
import { CategoriesAvatar, Checkbox } from "@aclymatepackages/atoms";

import useDisplayMeasurementSystem from "../../../helpers/hooks/displayMeasurementSystem";

const SeparateUtilitiesDefaultEmissionCategoryWarning = () => {
  const theme = useTheme();
  const { palette } = theme;

  return (
    <Tooltip title="This emission was created as a default emission since you haven't uploaded utilities for this month yet.">
      <Avatar
        style={{
          height: "20px",
          width: "20px",
          fontSize: "0.9rem",
          border: "solid thin white",
          color: "white",
          backgroundColor: palette.mileage.main,
          position: "absolute",
          top: "0",
          right: "0",
        }}
      >
        <WarningIcon style={{ fontSize: "inherit" }} />
      </Avatar>
    </Tooltip>
  );
};

const EmissionDetailsTableRow = ({
  transaction,
  setSelectedTransaction,
  onSelectRow,
  isSelected,
}) => {
  const { palette } = useTheme();

  const {
    date,
    name,
    source,
    subcategory,
    tonsCo2e = 0,
    electricRenewablesPercentage,
    taskDocId,
    vendorName,
    description,
    notes = [],
    id,
  } = transaction;

  const { convertCarbonUnits } = useDisplayMeasurementSystem();

  const isClickable =
    (source === "csv-upload" || source === "manual-entry" || taskDocId || id) &&
    setSelectedTransaction;

  const openTransactionsDetailSlider = () => {
    if (!isClickable) {
      return;
    }

    return setSelectedTransaction(transaction);
  };

  return (
    <TableRow
      key={name}
      hover={isClickable}
      onClick={() => openTransactionsDetailSlider()}
      style={{ cursor: isClickable ? "pointer" : "inherit" }}
    >
      {onSelectRow && (
        <TableCell padding="checkbox">
          <Checkbox value={isSelected} editValue={onSelectRow} />
        </TableCell>
      )}
      <TableCell scope="row">{formatDate(date)}</TableCell>
      <TableCell>{vendorName || name || description}</TableCell>
      <TableCell align="right">
        <Grid container direction="row">
          <Grid item>
            <Box position="relative">
              <CategoriesAvatar
                subcategory={subcategory}
                color={
                  electricRenewablesPercentage === 100 && palette.secondary.main
                }
                tooltip={
                  electricRenewablesPercentage === 100 &&
                  `${
                    source === "default-utilities" ? "We predict t" : "T"
                  }his utility emission was 100% offset by renewables.`
                }
              />
              {source === "default-utilities" && (
                <SeparateUtilitiesDefaultEmissionCategoryWarning />
              )}
            </Box>
          </Grid>
        </Grid>
      </TableCell>
      <TableCell align="center">
        {!!notes.length && (
          <Tooltip title={`${notes.length} note${notes.length > 1 ? "s" : ""}`}>
            <StickyNote2OutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="right">
        {formatDecimal(Math.abs(convertCarbonUnits(tonsCo2e)))}
      </TableCell>
    </TableRow>
  );
};

const EmissionsTable = ({
  headers = [],
  setSelectedTransaction,
  emissions,
  onSelectRow,
  selectedRows = [],
  noSort,
}) => {
  const { carbonUnitLabel } = useDisplayMeasurementSystem();

  const selectColumnHeader = onSelectRow ? [{}] : [];

  const tableLabelRows = !headers.length
    ? [
        ...selectColumnHeader,
        { label: "Date", align: "inherit" },
        { label: "Name", align: "inherit" },
        { label: "Category", align: "inherit" },
        { label: "Notes", align: "center" },
        { label: `${ucFirstLetters(carbonUnitLabel)} CO2`, align: "right" },
      ]
    : headers;

  const sortedEmissions = !noSort
    ? [...emissions].sort((a, b) => new Date(b.date) - new Date(a.date))
    : emissions;

  return (
    <TableContainer
      style={{
        maxHeight: "300px",
      }}
    >
      <Table
        style={{
          minWidth: 250,
          maxHeight: "300px",
        }}
        stickyHeader
      >
        <TableHead>
          <TableRow>
            {tableLabelRows.map(({ label, align }, idx) => (
              <TableCell key={`emissions-table-head-${idx}`} align={align}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEmissions.map((transaction, index) => (
            <EmissionDetailsTableRow
              transaction={transaction}
              setSelectedTransaction={setSelectedTransaction}
              key={`emissions-details-table-row-${index}`}
              onSelectRow={onSelectRow && onSelectRow(index)}
              isSelected={selectedRows[index]}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default EmissionsTable;
